// copy of packages/integrations/harbor/shared/src/instance-url.ts, kept close
// to it so the copies merge into one shared helper (INT-576). It differs in
// two ways, each a URL Harbor's rule lets through that no instance can be
// at: a host with an empty label (`https://.example.com`) and port 0 are
// refused.
//
// Like Harbor's, the rule takes an origin and nothing after it, so an Argo CD
// served under a root path (`server.rootpath`, as at
// https://example.com/argocd) isn't supported in v1. The connector's
// `instance` (`@p0security/p0-connector-argocd`) refuses one too, so P0 never
// stores a URL the connector would refuse.

/** What to enter when {@link isInstanceUrl} refuses a value. The message
 * doesn't repeat the value. The install framework's error for a refused field
 * does carry the field's value, to whoever sent it and to the log. */
export const INVALID_INSTANCE_URL =
  "Enter the instance's address as https://<host> or https://<host>:<port>, in lowercase and with nothing after it, such as https://argocd.example.com. Leave out a trailing slash, any path (Argo CD served under a root path isn't supported), any username or password, and port 443.";

// A copy of the private `toUrl` in
// packages/integrations/databricks/shared/src/components.ts, by way of
// Harbor's copy.
//
// `URL.parse` would do this, but the frontend bundles this file, and its
// browser targets (`build.target` in frontend/vite.config.mjs) predate it.
const toUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

// An origin spelled plainly: `https://`, a host of dot-separated labels of
// lowercase letters, digits and hyphens, none empty, with one trailing dot
// allowed, and, if it has one, a port other than 0. The URL has to be safe to
// write into Terraform as it stands, so nothing that could close a string or open an
// interpolation gets in. An IPv6 literal (`https://[::1]`) is refused on
// purpose: its brackets and colons are outside that set, and such an instance
// can be given a host name.
const PLAIN_ORIGIN_PATTERN =
  /^https:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*\.?(?::[1-9]\d*)?$/;

/**
 * Whether `url`, ignoring surrounding whitespace, is an Argo CD instance's
 * address in the one spelling P0 stores: `https://`, a lowercase host and, if
 * it has one, a port, with nothing after it.
 *
 * The Argo CD connector is more lenient. It takes any spelling of a bare
 * https origin and reduces it to the origin (`instance`, in
 * `@p0security/p0-connector-argocd`). P0 takes the origin itself and
 * nothing else, so that what it stores is what the connector calls: the URL
 * is fixed when the item is created, the install framework compares it as a
 * string, and each install check runs this rule over the stored value again.
 * So a trailing slash, an uppercase host, port 443 and an IP address that
 * isn't in dotted decimal are refused here, though the connector takes them.
 *
 * Plain http is refused because the API token goes to this address. A path,
 * a query, a fragment, a username and a password are refused because none is
 * part of an origin. So an Argo CD served under a root path isn't supported.
 *
 * Surrounding whitespace is ignored because the install framework trims a
 * value before it validates and stores it.
 */
export const isInstanceUrl = (url: unknown): url is string => {
  if (typeof url !== "string") return false;
  const candidate = url.trim();
  // The origin itself and no other spelling. The pattern alone would pass
  // `https://argocd.example.com:443` and `https://0x7f.1`, which aren't their
  // own origins.
  return (
    PLAIN_ORIGIN_PATTERN.test(candidate) &&
    toUrl(candidate)?.origin === candidate
  );
};
